/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactElement, ReactNode } from 'react'
import { Link as RRLink } from 'react-router-dom'
import styled, { css } from 'styled-components'
import { maskAccountNumber } from '@ally/aos-utils'
import {
  Account,
  AccountLOB,
  AccountStatus,
} from '@ally/data-types-afg-accounts'
import { ScreenReaderOnly, TextBody } from '@ally/metronome-ui'
import { useHostServices } from '../HostServices'
import { isRestrictedCCStatus } from '../utils'

const StyledWrapper = styled.div`
  ${({ theme: { media } }): string =>
    `
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 0;
    min-width: 100%;
    overflow: hidden;

    ${media.down('md')} {
      /* reveals the :focus-visible outline  */
      overflow: visible;
      /* shows the full outline of the focus */
      padding-top: 2px;
    }

    ${media.up('xl')} {
      display: inline;
    }
    `};
`

const StyledAnchor = styled(RRLink)(({ theme }) => theme.buttons.link)

const SharedAccountStyles = css`
  text-transform: capitalize;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%;
  vertical-align: middle;
  display: inline-block;

  @media print {
    display: inline;
  }
`

const StyledLink = styled(StyledAnchor)`
  ${({ theme: { colors } }): string => `
    ${SharedAccountStyles}

    @media print {
      color: ${colors['slate-5']};
    }
  `};
`

const StyledButton = styled.button(({ theme }) => theme.buttons.link)

export const StyledMortgageButton = styled(StyledButton)`
  ${({ theme: { colors, media } }): string =>
    `
    max-width: 100%;

    > span {
      text-overflow: ellipsis;
      overflow: hidden;
      max-width: 100%;
      vertical-align: middle;
      display: inline-block;

      ${media.down('md')} {
        width: 100%;
      }

      @media print {
        display: inline;
        color: ${colors['slate-5']};
      }
    }
  `};
`

export const StyledRestrictedAccount = styled(TextBody)`
  ${SharedAccountStyles}
`

export const AccountDetailsLink: React.FC<{
  className?: string
  text: string | ReactNode
  key?: string
  allytm?: string
  account: Account
  hasStaleData?: boolean
}> = ({
  account,
  className,
  text,
  key,
  allytm,
  hasStaleData,
}): ReactElement => {
  const { type, id, nickName, name, accountNumber } = account
  const { featureFlags, redirectToMortgage } = useHostServices()
  const acctDetailsFFisEnabled = featureFlags.variation(
    'FF_ally-next-account-details',
    ['DDA'],
  ) || ['DDA']

  const isAccountTypeSupported =
    Array.isArray(acctDetailsFFisEnabled) &&
    acctDetailsFFisEnabled.includes(type)

  const acctDetailsRoute =
    acctDetailsFFisEnabled && isAccountTypeSupported
      ? `/account/${id}/details`
      : `/bank/accounts/details/${id}`

  function accountDetailsUrl(accountType: string | undefined): string {
    if (hasStaleData) {
      return `/error`
    }
    switch (accountType) {
      // istanbul ignore next
      case 'INVS':
        return `/ext-invest-live/accounts/holdings-balances/${id}`
      // istanbul ignore next
      case 'INVW':
        return `/ext-wealth/account-details/${id}`
      // istanbul ignore next
      case 'INVA':
        return `/ext-invest-live/dashboard/${id}`
      case 'LOAN':
      case 'OPERATING_LEASE':
      case 'FINANC_LEASE':
      case 'HIRE_PURCHASE':
        // TODO: This URL will be updated in future sprints
        return '/dashboard'
      case 'PLATINUM':
      case 'EVERYDAY':
      case 'UNLIMITED':
        return `/sso/credit-card/Account/Overview?accountProxyId=${id}`
      // istanbul ignore next
      default:
        return acctDetailsRoute
    }
  }

  const sharedProps = {
    className,
    children: text,
    key,
    'data-allytmln': allytm,
    'aria-label': `${nickName || name} account ending in ${maskAccountNumber(
      accountNumber,
      'none',
    )}`,
  }

  if (account.lob === AccountLOB.MORTGAGE) {
    return (
      <StyledWrapper>
        <StyledMortgageButton
          {...sharedProps}
          data-testid="snapshot-mortgage-modal-button-trigger"
          onClick={(): void => redirectToMortgage(account.accountNumber)}
        />
      </StyledWrapper>
    )
  }

  const hasCurrentBalance = typeof account.balance?.current === 'number'
  const isClosedWithValue =
    account.status === AccountStatus.CLOSED && hasCurrentBalance

  if (
    account.lob === AccountLOB.CC &&
    isRestrictedCCStatus(account.status) &&
    !isClosedWithValue
  ) {
    return (
      <StyledWrapper>
        <StyledRestrictedAccount
          tag="span"
          size="sm"
          weight="bold"
          {...sharedProps}
          aria-label=""
          aria-hidden
        />
        <ScreenReaderOnly>
          {`${nickName || name} account ending in ${maskAccountNumber(
            accountNumber,
            'none',
          )}`}
        </ScreenReaderOnly>
      </StyledWrapper>
    )
  }

  return (
    <StyledWrapper>
      <StyledLink
        {...sharedProps}
        data-testid="account-link"
        to={accountDetailsUrl(type)}
      />
    </StyledWrapper>
  )
}
